import {
  CUSTOMER_LEVEL,
  IBulkPatchPayload,
  type ICancellationOptions,
  type IErrorApiResponse,
  type IPaginationResponse,
  type ISuccessApiResponse,
  type IValidationError,
} from '@wyz/types';
import {
  CrudOptions,
  OverridableQuery,
  UseMutationOptions,
  UseQueryOptions,
  restrictApi,
  serialize,
  useCrud,
  useMutation,
  useQuery,
} from '@wyz/utils';
import { useAutocompleteSearch } from '~/common/api';
import { useApi, useApplicationContext, useConfig } from '~/common/context';
import { ENTITIES_STATUSES } from '~/common/schemas';
import type { ISupplierTemplateSupplier } from '~/common/types';
import {
  CommonErrorCodesEnumType,
  IActiveAdvert,
  IContainerEligibleCustomerList,
  ICustomer,
  ICustomerDetail,
  IMatchedCustomer,
  IMinimalBrand,
  IUserCustomerPreview,
} from '~/common/types';
import { ConnectQueryKeys } from '~/connect/api';
import type {
  ICustomerAutocompleteFilterQuery,
  ICustomerCreatePayload,
  ICustomerUpdatePayload,
  IMatchedCustomerCriteriaFilter,
  IMinimalCustomer,
} from '~/connect/types';
import { ISearchTypeArgs } from '~/purchase/types';

type IContactTypeResponse = { authorizedTypes: Array<string> };

type IPaginateFilterQuery = {
  limit?: number;
  page?: number;
  customerIds?: Array<string>;
};

type Options = Pick<
  CrudOptions<
    ICustomer,
    ICustomerCreatePayload,
    ICustomerUpdatePayload,
    IErrorApiResponse<
      IValidationError<CommonErrorCodesEnumType>,
      CommonErrorCodesEnumType
    >
  >,
  'create' | 'update' | 'getOneById'
> & {
  paginateMatched?: OverridableQuery<
    UseQueryOptions<IPaginationResponse<IMatchedCustomer>, IErrorApiResponse>
  > & {
    args: IMatchedCustomerCriteriaFilter;
  };
  isInitialized?: OverridableQuery<
    UseQueryOptions<ISuccessApiResponse<boolean>, IErrorApiResponse>
  >;
  getLevels?: OverridableQuery<
    UseQueryOptions<ISuccessApiResponse<Array<number>>, IErrorApiResponse>
  >;
  getTypes?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<IContactTypeResponse>,
      IErrorApiResponse
    >
  >;
  getAdverts?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<Array<IActiveAdvert>>,
      IErrorApiResponse
    >
  > & {
    args: { customerPublicKey?: string };
  };
  getBrands?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<Array<IMinimalBrand>>,
      IErrorApiResponse
    >
  > & {
    args: {
      customerPublicKey?: string;
      ctx: ISearchTypeArgs['searchType'];
    };
  };
  autocomplete?: { enabled: boolean };
  getChildren?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<IUserCustomerPreview[]>,
      IErrorApiResponse
    >
  > & { args: { id: string } };
  getEligibleForContainerOrders?: OverridableQuery<
    UseQueryOptions<IContainerEligibleCustomerList, IErrorApiResponse>
  >;
  bulkPatch?: UseMutationOptions<
    ISuccessApiResponse,
    IErrorApiResponse,
    IBulkPatchPayload
  >;
  paginate?: OverridableQuery<
    UseQueryOptions<IPaginationResponse<ICustomer>, IErrorApiResponse>
  > & { args?: IPaginateFilterQuery };
  getDetail?: OverridableQuery<
    UseQueryOptions<ISuccessApiResponse<ICustomerDetail>, IErrorApiResponse>
  > & {
    args?: {
      customerId: string;
    };
  };
  getSuppliersByCustomer?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<ISupplierTemplateSupplier[]>,
      IErrorApiResponse
    >
  > & {
    args?: {
      customerId: string;
    };
  };
};

export const useManageCustomers = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { callCoreApi } = useApi();
  const { tenantCode } = useConfig();
  const { auth } = useApplicationContext();
  const { search } = useAutocompleteSearch<IMinimalCustomer>({
    pathName: `/tenants/api/wtb/tenants/${tenantCode}/customers/autocomplete`,
  });

  const autocompleteFetcher = async (
    query: string,
    options: {
      excluded?: Array<string>;
      authorizedLevels?: Array<number>;
      limit?: number;
    } & ICancellationOptions,
  ) => {
    const queryPayload: ICustomerAutocompleteFilterQuery = {
      statuses: [ENTITIES_STATUSES.ENABLED],
      searchValue: query,
      searchableFields: ['name', 'commercialName', 'code'],
      displayedField: 'name',
      limit: options.limit,
    };

    const response = await search(queryPayload, { signal: options.signal });
    const filteredResponse = response.data
      .filter((customer) =>
        options.excluded ? !options.excluded?.includes(customer.id) : true,
      )
      .filter((customer) => {
        return !options.authorizedLevels
          ? true
          : options.authorizedLevels.includes(customer.level);
      });

    return filteredResponse.map((customer) => {
      return {
        label: `${String(customer.name)} ${
          customer.commercialName ? ` - ${customer.commercialName}` : ''
        } (${String(customer.code)}) - ${customer.city}`,
        value: customer.id,
        level: customer.level,
        code: customer.code,
      };
    });
  };

  const api = useCrud<
    'create' | 'update' | 'getOneById',
    ICustomer,
    ICustomerCreatePayload,
    ICustomerUpdatePayload,
    IErrorApiResponse<
      IValidationError<CommonErrorCodesEnumType>,
      CommonErrorCodesEnumType
    >
  >(
    {
      baseKey: ConnectQueryKeys.customers.base(),
      caller: callCoreApi,
      endpoint: `/tenants/api/wtb/tenants/${tenantCode}/customers`,
    },
    {
      ...options,
      create: {
        ...options.create,
        keysToInvalidateOnSuccess: [ConnectQueryKeys.customers.base()],
      },
      update: {
        ...options.update,
        keysToInvalidateOnSuccess: () => [
          ConnectQueryKeys.customers.base(),
          ConnectQueryKeys.supplierTemplates.base(),
          ConnectQueryKeys.users.customersByUser(
            auth.userId as string,
            CUSTOMER_LEVEL.N1,
            [],
          ),
          ConnectQueryKeys.changeLogs.paginate('Entity'),
        ],
      },
    },
  );

  const bulkPatchMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse>(
        `/tenants/api/wtb/tenants/${tenantCode}/customers`,
        {
          method: 'PATCH',
          body: JSON.stringify(variables),
        },
      ),
    ...options.bulkPatch,
    keysToInvalidateOnSuccess: [
      ConnectQueryKeys.customers.base(),
      ConnectQueryKeys.supplierTemplates.base(),
      ConnectQueryKeys.users.customersByUser(
        auth.userId as string,
        CUSTOMER_LEVEL.N1,
        [],
      ),
    ],
  });
  const isInitializedQuery = useQuery({
    queryKey: ConnectQueryKeys.customers.isInitialized(tenantCode),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<boolean>>(
        `/tenants/api/wtb/tenants/${tenantCode}/customers/is-initialized`,
      ),
    refetchOnWindowFocus: false,
    enabled: false,
    ...options.isInitialized,
  });

  const getAdvertsQuery = useQuery({
    queryKey: ConnectQueryKeys.customers.adverts(
      options.getAdverts?.args.customerPublicKey as string,
    ),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<Array<IActiveAdvert>>>(
        `/tenants/api/wtb/tenants/${tenantCode}/customers/${options.getAdverts?.args.customerPublicKey}/adverts`,
      ),
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    ...options.getAdverts,
    enabled: Boolean(options.getAdverts?.args.customerPublicKey),
  });

  const getBrandsQuery = useQuery({
    queryKey: ConnectQueryKeys.customers.brands(
      options.getBrands?.args.customerPublicKey as string,
      options.getBrands?.args.ctx as string,
    ),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<Array<IMinimalBrand>>>(
        `/tenants/api/wtb/tenants/${tenantCode}/customers/${options.getBrands?.args.customerPublicKey}/brands/${options.getBrands?.args.ctx as ISearchTypeArgs['searchType']}`,
      ),
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    ...options.getBrands,
    enabled:
      Boolean(options.getBrands?.args.customerPublicKey) &&
      Boolean(options.getBrands?.args.ctx),
  });

  const getLevelsQuery = useQuery({
    queryKey: ConnectQueryKeys.customers.levels(),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<Array<number>>>(
        `/tenants/api/wtb/tenants/${tenantCode}/customers/levels`,
      ),
    refetchOnWindowFocus: false,
    enabled: false,
    ...options.getLevels,
  });

  const getTypesQuery = useQuery({
    refetchOnWindowFocus: false,
    enabled: false,
    ...options.getTypes,
    queryKey: ConnectQueryKeys.customers.types(),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<IContactTypeResponse>>(
        `/tenants/api/wtb/tenants/${tenantCode}/contact-types`,
      ),
  });

  const hasSelectedCriteria =
    Boolean(options.paginateMatched?.args.criteria?.length) &&
    options.paginateMatched?.args.criteria.every(
      (criterion) => criterion.criterion && criterion.values.length,
    );

  const matchedQuery = useQuery({
    enabled: hasSelectedCriteria,
    ...options.paginateMatched,
    queryKey: ConnectQueryKeys.customers.matchedByCriteria(
      options.paginateMatched?.args,
    ),
    queryFn: () =>
      callCoreApi<IPaginationResponse<IMatchedCustomer>>(
        `/tenants/api/wtb/tenants/${tenantCode}/customers/matched?${serialize(
          options.paginateMatched?.args
            ? {
                ...options.paginateMatched.args,
                matchUserContext: options.paginateMatched.args.matchUserContext
                  ? 1
                  : 0,
              }
            : undefined,
        )}`,
      ),
  });

  const getChildrenQuery = useQuery({
    refetchOnWindowFocus: false,
    ...options.getChildren,
    enabled:
      Boolean(options.getChildren?.enabled) &&
      Boolean(options.getChildren?.args.id),
    queryKey: ConnectQueryKeys.customers.children(
      options.getChildren?.args.id as string,
    ),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<IUserCustomerPreview[]>>(
        `/tenants/api/wtb/tenants/${tenantCode}/customers/${options.getChildren?.args.id}/children`,
      ),
  });

  const getEligibleForContainerOrdersQuery = useQuery({
    refetchOnWindowFocus: false,
    ...options.getEligibleForContainerOrders,
    enabled: Boolean(options.getEligibleForContainerOrders?.enabled),
    queryKey: ConnectQueryKeys.customers.getEligibleForContainerOrders(),
    queryFn: () =>
      callCoreApi<IContainerEligibleCustomerList>(
        `/tenants/api/wtb/tenants/${tenantCode}/customers/eligible-for-container-orders`,
      ),
  });

  const paginate = useQuery({
    queryKey: [...ConnectQueryKeys.customers.base(), options.paginate?.args],
    queryFn: () =>
      callCoreApi<IPaginationResponse<ICustomer>>(
        `/tenants/api/wtb/tenants/${tenantCode}/customers?${serialize(
          options.paginate?.args ? options.paginate.args : {},
        )}`,
      ),
    refetchOnWindowFocus: false,
    ...options.paginate,
    enabled: Boolean(options.paginate?.enabled),
  });

  const getDetailQuery = useQuery({
    queryKey: [
      ...ConnectQueryKeys.customers.detail(
        options.getDetail?.args?.customerId as string,
      ),
    ],
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<ICustomerDetail>>(
        `/tenants/api/wtb/tenants/${tenantCode}/customers/${options.getDetail?.args?.customerId}/detail`,
      ),
    ...options.getDetail,
    enabled:
      Boolean(options.getDetail?.args?.customerId) &&
      Boolean(options.getDetail?.enabled),
  });

  const getSuppliersByCustomerQuery = useQuery({
    queryKey: [
      ...ConnectQueryKeys.customers.base(),
      'matched-suppliers',
      options.getSuppliersByCustomer?.args?.customerId,
    ],
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<ISupplierTemplateSupplier[]>>(
        `/tenants/api/wtb/tenants/${tenantCode}/customers/${options.getSuppliersByCustomer?.args?.customerId}/redistribution-supplier-templates/default-suppliers`,
      ),
    ...options.getSuppliersByCustomer,
    enabled:
      Boolean(options.getSuppliersByCustomer?.args?.customerId) &&
      Boolean(options.getSuppliersByCustomer?.enabled),
  });

  return restrictApi(options, {
    ...api,
    isInitialized: isInitializedQuery,
    getLevels: getLevelsQuery,
    getAdverts: getAdvertsQuery,
    getBrands: getBrandsQuery,
    getTypes: getTypesQuery,
    paginateMatched: matchedQuery,
    autocomplete: autocompleteFetcher,
    getChildren: getChildrenQuery,
    bulkPatch: bulkPatchMutation,
    getEligibleForContainerOrders: getEligibleForContainerOrdersQuery,
    paginate,
    getDetail: getDetailQuery,
    getSuppliersByCustomer: getSuppliersByCustomerQuery,
  });
};
